import type { LeadCloudFileType } from '@econtractor/types';

export const FOLDER_PATH_MAX = 500;
export const FILE_NAME_MAX = 255;

export const normalizeFolderPath = (value: unknown): string => {
    const raw = String(value || '')
        .replace(/\\/g, '/')
        .trim();
    const parts = raw
        .split('/')
        .map((part) => part.trim())
        .filter(Boolean);
    if (parts.some((part) => part === '.' || part === '..')) return '';
    return parts.join('/');
};

export const isValidFolderName = (name: unknown): boolean => {
    const trimmed = String(name || '').trim();
    if (!trimmed) return false;
    if (/[\\/]/.test(trimmed)) return false;
    if (trimmed === '.' || trimmed === '..') return false;
    return true;
};

export const joinFolderPath = (parentPath: unknown, name: unknown): string => {
    const parent = normalizeFolderPath(parentPath);
    const safeName = String(name || '').trim();
    return parent ? `${parent}/${safeName}` : safeName;
};

export const parentFolderPath = (folderPath: unknown): string => {
    const path = normalizeFolderPath(folderPath);
    if (!path) return '';
    const slash = path.lastIndexOf('/');
    return slash < 0 ? '' : path.slice(0, slash);
};

export const folderNameFromPath = (folderPath: unknown): string => {
    const path = normalizeFolderPath(folderPath);
    if (!path) return '';
    const slash = path.lastIndexOf('/');
    return slash < 0 ? path : path.slice(slash + 1);
};

export const isCloudFolder = (item: LeadCloudFileType | null | undefined): boolean => Boolean(item?.isFolder);

export const itemFolderPath = (item: LeadCloudFileType | null | undefined): string =>
    normalizeFolderPath(item?.folderPath);

export const isPathInsideFolder = (itemPath: unknown, folderPath: unknown): boolean => {
    const folder = normalizeFolderPath(folderPath);
    const item = normalizeFolderPath(itemPath);
    if (!folder) return false;
    return item === folder || item.startsWith(`${folder}/`);
};

export const replaceFolderPathPrefix = (folderPath: unknown, oldPrefix: string, newPrefix: string): string => {
    const path = normalizeFolderPath(folderPath);
    const from = normalizeFolderPath(oldPrefix);
    const to = normalizeFolderPath(newPrefix);
    if (!path || !from) return path;
    if (path === from) return to;
    if (path.startsWith(`${from}/`)) return `${to}${path.slice(from.length)}`;
    return path;
};

export const listCurrentFolders = (files: LeadCloudFileType[], currentPath: string): LeadCloudFileType[] => {
    const path = normalizeFolderPath(currentPath);
    return files.filter((item) => isCloudFolder(item) && parentFolderPath(item.folderPath) === path);
};

export const listCurrentFiles = (files: LeadCloudFileType[], currentPath: string): LeadCloudFileType[] => {
    const path = normalizeFolderPath(currentPath);
    return files.filter((item) => !isCloudFolder(item) && itemFolderPath(item) === path);
};

export const siblingNamesAtPath = (files: LeadCloudFileType[], currentPath: string): string[] => {
    const folders = listCurrentFolders(files, currentPath).map((item) => item.fileName);
    const fileNames = listCurrentFiles(files, currentPath).map((item) => item.fileName);
    return [...folders, ...fileNames];
};

export const folderExists = (files: LeadCloudFileType[], folderPath: string): boolean => {
    const path = normalizeFolderPath(folderPath);
    if (!path) return true;
    return files.some((item) => isCloudFolder(item) && itemFolderPath(item) === path);
};

export const nearestExistingPath = (files: LeadCloudFileType[], folderPath: string): string => {
    let path = normalizeFolderPath(folderPath);
    while (path && !folderExists(files, path)) {
        path = parentFolderPath(path);
    }
    return path;
};

export const breadcrumbSegments = (currentPath: string): { label: string; path: string }[] => {
    const path = normalizeFolderPath(currentPath);
    if (!path) return [];
    const parts = path.split('/');
    return parts.map((label, index) => ({
        label,
        path: parts.slice(0, index + 1).join('/'),
    }));
};
